<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn color="primary" x-small text :disabled="submittingForm" @click="goBack">
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ editing ? "Editando" : "Criando" }} Matrícula
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form
        class="pa-6"
        :disabled="submittingForm || isReadOnly"
        @submit.prevent="handleSubmit(submitForm)"
      >
        <v-row
          v-for="(matricula, index) in form.matriculas"
          :key="index"
          class="mb-6 pa-6 elevation-1"
        >
          <v-col :cols="form.matriculas.length > 1 ? 11 : 12">
            <v-row>
              <v-col cols="12" md="6" lg="4">
                <e-label>Aluno</e-label>

                <e-autocomplete
                  :items="alunosList"
                  :loading="carregandoAlunos"
                  :item-value="(a) => a.id"
                  :item-text="(a) => `${a.nomecompleto} (${a.codigo})`"
                  :disabled="editing || isReadOnly"
                  v-model="matricula.aluno_id"
                  dense
                  solo
                  label="Selecione"
                  @change="(val) => verificarAluno(matricula, val)"
                />
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <e-label>Turma</e-label>

                <e-autocomplete
                  :items="turmasList"
                  :loading="carregandoTurmas"
                  :item-text="(t) => t.descricao"
                  return-object
                  :disabled="editing || isReadOnly"
                  v-model="matricula.turma"
                  dense
                  solo
                  label="Selecione"
                  @change="(val) => changeTurma(matricula, val)"
                />
              </v-col>

              <v-col cols="12" md="6" lg="3">
                <e-label>Série</e-label>

                <e-autocomplete
                  :items="matricula.turma?.series || []"
                  :item-value="(s) => s.id"
                  :item-text="(s) => s.descricao"
                  :disabled="!matricula.turma || isReadOnly"
                  v-model="matricula.serie_id"
                  dense
                  solo
                  label="Selecione"
                />
              </v-col>

              <v-col cols="12" md="6" lg="2">
                <e-label>Transporte</e-label>

                <v-switch
                  v-model="matricula.utiliza_transporte_publico"
                  color="primary"
                  hide-details
                  :disabled="isReadOnly"
                  :label="matricula.utiliza_transporte_publico ? 'Utiliza' : 'Não utiliza'"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="1" class="my-auto">
            <v-btn
              v-if="form.matriculas.length > 1 && !isReadOnly"
              color="error"
              small
              @click="removerMatricula(matricula)"
            >
              <v-icon small>fa-trash</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <div class="d-flex">
              <v-btn
                color="primary"
                type="submit"
                :disabled="disabled || submittingForm || isReadOnly"
                :loading="submittingForm"
              >
                Enviar Dados
              </v-btn>

              <v-btn
                v-if="!editing && !isReadOnly"
                color="secondary"
                class="ml-3"
                @click="adicionarMatricula"
              >
                Adicionar
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>

    <e-sheet-aviso-matricula-existente
      :sheet="sheet"
      :matricula="matricula"
      @changeSheet="sheet = $event"
    />
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";
import ESheetAvisoMatriculaExistente from "../../components/Matriculas/ESheetAvisoMatriculaExistente.vue";
import Aluno from "../../Models/Aluno";

export default {
  components: {
    ELabel,
    ESheetAvisoMatriculaExistente,
  },

  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters("Auth", ["user"]),

    canEditSerie() {
      return ["admin", "super-admin", "aux-admin", "ti"].includes(this.user?.roleName);
    },

    isReadOnly() {
      return !this.canEditSerie;
    },
  },

  watch: {
    turmasList(val) {
      if (val.length && this.editing) {
        this.loadFormData();
      }
    },
    user(val) {
      if (val) this.loadData();
    },
  },

  mounted() {
    if (this.user) this.loadData();
  },

  data() {
    return {
      alunosList: [],
      carregandoAlunos: false,
      carregandoTurmas: false,
      carregandoMatriculas: false,
      disabled: true,
      form: {
        matriculas: [this.getDefaultMatricula()],
      },
      matricula: {},
      matriculasList: [],
      submittingForm: false,
      sheet: false,
      turmasList: [],
    };
  },

  methods: {
    goBack() {
      this.$router.push({ name: "matriculas" });
    },

    getDefaultMatricula() {
      return {
        aluno_id: null,
        turma_id: null,
        serie_id: null,
        utiliza_transporte_publico: false,
      };
    },

    removerMatricula(matricula) {
      this.form.matriculas = this.form.matriculas.filter((m) => m !== matricula);
    },

    adicionarMatricula() {
      this.form.matriculas.push(this.getDefaultMatricula());
    },

    changeTurma(matricula, turma) {
      if (!turma || !turma.id) {
        matricula.turma = null;
        matricula.turma_id = null;
        matricula.serie_id = null;

        return;
      }

      matricula.turma = turma;
      matricula.turma_id = turma.id;

      if (!Array.isArray(turma.series)) turma.series = [];

      if (turma.series.length) {
        matricula.serie_id = turma.series[0].id;
      }
    },

    verificarAluno(matricula, alunoId) {
      this.verificarSeAlunoPossuiMatriculaNoMunicipio(alunoId);
    },

    async verificarSeAlunoPossuiMatriculaNoMunicipio(alunoId) {
      try {
        const response =
          await this.$services.matriculasService.verificarSeAlunoPossuiMatriculaNoMunicipio(
            alunoId
          );
        if (!response) {
          this.sheet = false;
          this.disabled = false;
          return;
        }
        this.form.matriculas = this.form.matriculas.filter(
          (matricula) => matricula.aluno_id !== alunoId
        );
        this.matricula = response;
        this.sheet = true;
        this.disabled = true;
      } catch {
        this.disabled = true;
      }
    },

    async submitForm() {
      this.submittingForm = true;
      try {
        if (this.editing) {
          await this.$services.matriculasService.atualizarMatricula(this.form.matriculas[0]);
        } else {
          await this.$services.matriculasService.matricular(this.form);
        }
        this.$toast.success("Matricula Atualizada com sucesso");
        this.$router.push({ name: "matriculas" });
      } catch (error) {
        this.$handleError(error);
      }
      this.submittingForm = false;
    },

    async listarAlunos() {
      this.carregandoAlunos = true;
      try {
        const res = await this.$services.alunosService.syncAll();
        this.alunosList = res.data.map((aluno) => new Aluno(aluno));
      } finally {
        this.carregandoAlunos = false;
      }
    },

    async loadTurmas() {
      this.carregandoTurmas = true;
      try {
        this.turmasList = await this.$services.turmasService.syncAll();
      } finally {
        this.carregandoTurmas = false;
      }
    },

    async listarMatriculas() {
      this.carregandoMatriculas = true;
      try {
        this.matriculasList = await this.$services.matriculasService.all();
      } finally {
        this.carregandoMatriculas = false;
      }
    },

    async loadData() {
      this.$loaderService.open("Carregando dados");
      try {
        await Promise.all([this.listarAlunos(), this.loadTurmas(), this.listarMatriculas()]);
        this.loadFormData();
      } catch (error) {
        this.$handleError(error);
      }
      this.$loaderService.close();
    },

    loadFormData() {
      const { matricula_id } = this.$route.params;
      if (!matricula_id) return;

      this.disabled = false;

      const matriculas = this.matriculasList.filter((matricula) => matricula.id === matricula_id);

      this.form.matriculas = matriculas.map((matricula) => {
        const turma = this.turmasList.find((turma) => turma.id === matricula.turma_id);
        if (turma) {
          matricula.turma = turma;
          if (!Array.isArray(turma.series)) turma.series = [];

          if ((!matricula.serie_id || this.canEditSerie) && turma.series.length) {
            matricula.serie_id = matricula.serie_id || turma.series[0].id;
          }
        }
        return matricula;
      });
    },
  },
};
</script>

<style></style>
